import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  ShoppingCart,
  People,
  Euro,
  DeliveryDining,
  Receipt,
} from '@mui/icons-material';
import { firebaseAdminService } from '../../services/firebaseAdminService';
import { useCurrency } from '../../hooks/useCurrency';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const font = '"Inter", sans-serif';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const money = useCurrency();
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersData, setOrdersData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);

  // Admin role is provisioned at signup via Supabase JWT app_metadata.
  // No client-side mirror table needed any more.

  // Fetch real data from Firebase
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [ordersResult, usersResult] = await Promise.all([
          firebaseAdminService.getOrders(),
          firebaseAdminService.getUsers(),
        ]);

        const orders = ordersResult || [];
        const users = usersResult || [];
        setOrdersData(orders);
        setUsersData(users);

        // Parse and sort orders for recent list
        const parseDate = (d: any): Date => {
          if (d?.toDate) return d.toDate();
          if (d?.seconds) return new Date(d.seconds * 1000);
          if (typeof d === 'string') return new Date(d);
          return new Date(0);
        };

        const sortedOrders = [...orders].sort((a, b) => parseDate(b.created_at).getTime() - parseDate(a.created_at).getTime());

        setRecentOrders(sortedOrders.slice(0, 8).map((order: any) => {
          const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || order.total_amount || '0');
          const customerName = order.customer_name || order.customerName || order.customer?.name || order.name || 'Walk-in';
          const orderId = order.order_number || order.orderNumber || order.id || 'N/A';
          const orderDate = parseDate(order.created_at);
          const orderType = (order.order_type || order.delivery_type || order.type || '').toLowerCase();

          return {
            id: orderId,
            customer: customerName,
            amount: orderTotal,
            status: order.status || 'pending',
            date: orderDate,
            type: orderType.includes('dine') ? t('dashboard.dineIn') : orderType.includes('take') ? t('dashboard.takeout') : orderType.includes('deliv') ? t('dashboard.delivery') : t('dashboard.other'),
          };
        }));
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // KPI calculations
  const kpis = React.useMemo(() => {
    const totalOrders = ordersData.length;
    const totalRevenue = ordersData.reduce((sum: number, o: any) => {
      const val = typeof o.total === 'number' ? o.total : parseFloat(o.total || o.total_amount || '0');
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const customers = usersData.filter((u: any) => (u.role || '').toLowerCase() === 'customer').length;
    const riders = usersData.filter((u: any) => (u.role || '').toLowerCase() === 'rider' && u.is_active !== false).length;

    return [
      { title: t('dashboard.totalOrders'), value: totalOrders.toLocaleString(), icon: ShoppingCart, color: '#1976D2', bg: 'rgba(33, 150, 243, 0.12)' },
      { title: t('dashboard.revenue'), value: money.fmt(totalRevenue), icon: Euro, color: '#4CAF50', bg: 'rgba(76, 175, 80, 0.12)' },
      { title: t('dashboard.avgOrder'), value: money.fmt(avgOrder), icon: Receipt, color: '#ed6c02', bg: 'rgba(255, 152, 0, 0.12)' },
      { title: t('dashboard.customers'), value: customers.toLocaleString(), icon: People, color: '#9c27b0', bg: 'rgba(156, 39, 176, 0.12)' },
      { title: t('dashboard.activeRiders'), value: riders.toLocaleString(), icon: DeliveryDining, color: '#0288d1', bg: '#e1f5fe' },
    ];
  }, [ordersData, usersData]);

  // Order types chart
  const orderTypesData = React.useMemo(() => {
    if (!ordersData.length) return [];
    const dineInLabel = t('dashboard.dineIn');
    const takeoutLabel = t('dashboard.takeout');
    const deliveryLabel = t('dashboard.delivery');
    const counts: Record<string, number> = { [dineInLabel]: 0, [takeoutLabel]: 0, [deliveryLabel]: 0 };
    ordersData.forEach((o: any) => {
      const ot = (o.order_type || o.delivery_type || o.type || '').toLowerCase();
      if (ot.includes('dine')) counts[dineInLabel]++;
      else if (ot.includes('take') || ot.includes('away')) counts[takeoutLabel]++;
      else if (ot.includes('deliv')) counts[deliveryLabel]++;
    });
    const colors: Record<string, string> = { [dineInLabel]: '#1976D2', [takeoutLabel]: '#FF9800', [deliveryLabel]: '#4CAF50' };
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value, color: colors[name] }));
  }, [ordersData]);

  // Payment methods chart
  const paymentMethodsData = React.useMemo(() => {
    if (!ordersData.length) return [];
    const amounts: Record<string, number> = { Cash: 0, Card: 0, PayPal: 0 };
    ordersData.forEach((o: any) => {
      const total = typeof o.total === 'number' ? o.total : parseFloat(o.total || '0');
      const pm = (o.payment_method || o.paymentMethod || o.payment_type || '').toLowerCase().trim();
      if (pm === 'card' || pm === 'credit_card' || pm === 'debit_card' || pm === 'online' || pm === 'credit card' || pm === 'debit card') amounts.Card += total;
      else if (pm === 'paypal' || pm === 'pay_pal') amounts.PayPal += total;
      else amounts.Cash += total;
    });
    const colors: Record<string, string> = { Cash: '#1976D2', Card: '#4CAF50', PayPal: '#FF9800' };
    return Object.entries(amounts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100, color: colors[name] }));
  }, [ordersData]);

  // Weekly sales (last 7 days)
  const weeklySalesData = React.useMemo(() => {
    if (!ordersData.length) return [];
    const parseDate = (d: any): string => {
      if (d?.toDate) return d.toDate().toISOString().split('T')[0];
      if (d?.seconds) return new Date(d.seconds * 1000).toISOString().split('T')[0];
      if (typeof d === 'string') return new Date(d).toISOString().split('T')[0];
      return '';
    };
    const last7 = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      return d.toISOString().split('T')[0];
    });
    return last7.map(date => {
      const dayOrders = ordersData.filter((o: any) => parseDate(o.created_at) === date);
      const revenue = dayOrders.reduce((s: number, o: any) => s + (typeof o.total === 'number' ? o.total : parseFloat(o.total || '0')), 0);
      return {
        day: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        orders: dayOrders.length,
        revenue: parseFloat(revenue.toFixed(2)),
      };
    });
  }, [ordersData]);

  // Top products
  const topProducts = React.useMemo(() => {
    if (!ordersData.length) return [];
    const itemMap = new Map<string, { name: string; sold: number; revenue: number }>();
    ordersData.forEach((order: any) => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const name = item.name || item.item_name || item.product_name || 'Unknown';
          const qty = parseFloat(item.quantity || 1);
          const price = parseFloat(item.price || item.price_with_tax || item.unit_price || 0);
          const existing = itemMap.get(name) || { name, sold: 0, revenue: 0 };
          existing.sold += qty;
          existing.revenue += qty * price;
          itemMap.set(name, existing);
        });
      }
    });
    const sorted = Array.from(itemMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
    const maxSold = sorted.length > 0 ? Math.max(...sorted.map(p => p.sold)) : 1;
    return sorted.map(p => ({ ...p, revenue: Math.round(p.revenue * 100) / 100, maxSold }));
  }, [ordersData]);

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'delivered' || s === 'completed') return { bg: 'rgba(76, 175, 80, 0.12)', color: '#4CAF50' };
    if (s === 'confirmed' || s === 'preparing') return { bg: 'rgba(33, 150, 243, 0.12)', color: '#2196F3' };
    if (s === 'on_delivery' || s === 'on delivery') return { bg: 'rgba(255, 152, 0, 0.12)', color: '#FF9800' };
    if (s === 'cancelled' || s === 'canceled') return { bg: 'rgba(244, 67, 54, 0.12)', color: '#F44336' };
    return { bg: '#1e2139', color: '#616161' };
  };

  // Shared paper style
  const paperSx = {
    bgcolor: '#1a1d2e',
    p: { xs: 1.5, md: 3 },
    border: '1px solid #3a3d52',
    borderRadius: 3,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    height: '100%',
  };

  const sectionTitle = (text: string) => (
    <Typography variant="h6" sx={{ color: '#e0e0e0', fontWeight: 700, mb: 2.5, fontFamily: font, fontSize: '1rem' }}>
      {text}
    </Typography>
  );

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 1.5, md: 4 } }}>
          <Skeleton variant="text" width={280} height={48} sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Grid item xs={12} sm={6} md key={i}><Skeleton variant="rounded" height={88} /></Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}><Skeleton variant="rounded" height={320} /></Grid>
            <Grid item xs={12} md={4}><Skeleton variant="rounded" height={320} /></Grid>
          </Grid>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 4 } }}>
        <Typography variant="h4" sx={{ color: '#e0e0e0', fontWeight: 700, mb: 3, fontFamily: font }}>
          {t('dashboard.title')}
        </Typography>

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <Grid item xs={12} sm={6} md key={i}>
                <Paper sx={{
                  p: 2,
                  border: '1px solid #3a3d52',
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2,
                    backgroundColor: kpi.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon sx={{ fontSize: 22, color: kpi.color }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: font, fontWeight: 500, display: 'block', lineHeight: 1.2 }}>
                      {kpi.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: font, color: '#e0e0e0', lineHeight: 1.3, fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {kpi.value}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Row 1: Weekly Sales + Order Types */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Weekly Revenue Chart */}
          <Grid item xs={12} md={8}>
            <Paper sx={paperSx}>
              {sectionTitle(t('dashboard.weeklyRevenue'))}
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={weeklySalesData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3d52" vertical={false} />
                  <XAxis dataKey="date" stroke="#999" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#999" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}
                    formatter={(value: number, name: string) => {
                      if (name === 'Revenue') return [`€${value.toFixed(2)}`, name];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Bar dataKey="revenue" fill="#1976D2" name="Revenue" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="orders" fill="#e0e0e0" name="Orders" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Order Types */}
          <Grid item xs={12} md={4}>
            <Paper sx={paperSx}>
              {sectionTitle(t('dashboard.orderTypes'))}
              {orderTypesData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={orderTypesData} cx="50%" cy="50%" outerRadius={70} innerRadius={40} dataKey="value" paddingAngle={3} strokeWidth={0}>
                        {orderTypesData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: 8, fontSize: 13 }}
                        formatter={(value: number, name: string) => {
                          const total = orderTypesData.reduce((s, e) => s + e.value, 0);
                          return [`${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {orderTypesData.map((entry, i) => {
                      const total = orderTypesData.reduce((s, e) => s + e.value, 0);
                      const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                      return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color }} />
                            <Typography variant="body2" sx={{ fontFamily: font, color: '#9ca3af' }}>{entry.name}</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontFamily: font, fontWeight: 600, color: '#e0e0e0' }}>{entry.value} ({pct}%)</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Typography sx={{ color: '#6b7280', textAlign: 'center', py: 6, fontFamily: font }}>{t('dashboard.noOrderData')}</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Row 2: Payment Methods + Top Products */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Payment Methods */}
          <Grid item xs={12} md={4}>
            <Paper sx={paperSx}>
              {sectionTitle(t('dashboard.paymentMethods'))}
              {paymentMethodsData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={paymentMethodsData} cx="50%" cy="50%" outerRadius={70} innerRadius={40} dataKey="value" paddingAngle={3} strokeWidth={0}>
                        {paymentMethodsData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: 8, fontSize: 13 }}
                        formatter={(value: number, name: string) => [`€${value.toFixed(2)}`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {paymentMethodsData.map((entry, i) => {
                      const total = paymentMethodsData.reduce((s, e) => s + e.value, 0);
                      const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                      return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color }} />
                            <Typography variant="body2" sx={{ fontFamily: font, color: '#9ca3af' }}>{entry.name}</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontFamily: font, fontWeight: 600, color: '#e0e0e0' }}>€{entry.value.toFixed(2)} ({pct}%)</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Typography sx={{ color: '#6b7280', textAlign: 'center', py: 6, fontFamily: font }}>{t('dashboard.noPaymentData')}</Typography>
              )}
            </Paper>
          </Grid>

          {/* Top Products */}
          <Grid item xs={12} md={8}>
            <Paper sx={paperSx}>
              {sectionTitle(t('dashboard.topSellingProducts'))}
              {topProducts.length === 0 ? (
                <Typography sx={{ color: '#6b7280', textAlign: 'center', py: 4, fontFamily: font }}>{t('dashboard.noProductData')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {topProducts.map((product: any, i: number) => (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: font, fontWeight: 700, minWidth: 20 }}>
                            {i + 1}.
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#e0e0e0', fontFamily: font, fontWeight: 600 }}>
                            {product.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: font }}>{product.sold} {t('dashboard.sold')}</Typography>
                          <Typography variant="body2" sx={{ color: '#e0e0e0', fontFamily: font, fontWeight: 700 }}>€{product.revenue.toFixed(2)}</Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={product.maxSold > 0 ? (product.sold / product.maxSold) * 100 : 0}
                        sx={{
                          height: 6, borderRadius: 3, bgcolor: 'rgba(0, 200, 150,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: '#1976D2', borderRadius: 3 },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Row 3: Recent Orders */}
        <Paper sx={paperSx}>
          {sectionTitle(t('dashboard.recentOrders'))}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[t('dashboard.order'), t('dashboard.customer'), t('common.type'), t('common.date'), t('common.amount'), t('common.status')].map(h => (
                    <TableCell key={h} sx={{ color: '#9ca3af', fontWeight: 600, fontFamily: font, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #3a3d52', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#6b7280', fontFamily: font }}>{t('dashboard.noRecentOrders')}</TableCell>
                  </TableRow>
                ) : (
                  recentOrders.map((order) => {
                    const sc = getStatusColor(order.status);
                    return (
                      <TableRow key={order.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' }, '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: font, fontSize: '0.82rem', borderBottom: '1px solid #f5f5f5' }}>
                          {typeof order.id === 'string' && order.id.length > 12 ? `${order.id.slice(0, 12)}...` : order.id}
                        </TableCell>
                        <TableCell sx={{ color: '#9ca3af', fontFamily: font, fontSize: '0.82rem', borderBottom: '1px solid #f5f5f5' }}>
                          {order.customer}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #f5f5f5' }}>
                          <Chip label={order.type} size="small" sx={{ fontFamily: font, fontWeight: 500, fontSize: '0.7rem', height: 22, bgcolor: 'rgba(0, 200, 150,0.08)', color: '#9ca3af' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#9ca3af', fontFamily: font, fontSize: '0.82rem', borderBottom: '1px solid #f5f5f5' }}>
                          {order.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: font, fontSize: '0.82rem', borderBottom: '1px solid #f5f5f5' }}>
                          €{order.amount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #f5f5f5' }}>
                          <Chip
                            label={order.status.replace(/_/g, ' ')}
                            size="small"
                            sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontFamily: font, fontSize: '0.7rem', textTransform: 'capitalize', height: 22 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default Dashboard;
